// Month-Day-Year formatter used by watchlist rows. Different from the
// existing fmtMDY inside the report modal — that one is M/D/YY, this
// one reads "Dec 31, 2027".
export const fmtResolveDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// In a resolution date, a timestamp at exactly midnight UTC ("…T00:00:00Z") is a date in
// disguise: the server writes day-granular resolutions that way as well as bare "YYYY-MM-DD".
const BARE_MIDNIGHT = /T00:00(:00)?(\.0+)?Z?$/;

// "MM/DD/YY", for the Question panel's dates. A bare "YYYY-MM-DD" prints as that day in every
// zone, and so does a bare-midnight timestamp when `calendarDay` says the value is a day (a
// resolution date). Any other timestamp is an instant and prints as the viewer's local day.
export const fmtShortDate = (iso, { calendarDay = false } = {}) => {
  if (!iso) return "—";
  const s = String(iso);
  const isDay = s.length === 10 || (calendarDay && BARE_MIDNIGHT.test(s));
  const d = new Date(isDay ? s.slice(0, 10) + "T00:00:00" : s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
};

// Full local date-time for disambiguating reforecast snapshots:
// "Jul 8, 2026, 2:18 PM". A date-only "YYYY-MM-DD" is parsed as LOCAL midnight
// (same T00:00:00 convention as fmtResolveDate) so a bare date can't shift a day
// in non-UTC zones. Used by the timeline scrub pill and the report-modal carousel.
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true, // pin 12-hour output; Intl's default can vary by env/locale settings
  });
};

// Parse a resolution date for comparison against effectiveToday (which is
// local — derived from a `datetime-local` picker). A date-only "YYYY-MM-DD"
// string passed to `new Date()` is interpreted as UTC midnight, which in
// non-UTC timezones can mark the date "past" hours early. Append T00:00:00 so
// it's parsed as LOCAL midnight — same convention as fmtResolveDate above.
export const parseResolveDateLocal = (s) =>
  s ? new Date(typeof s === "string" && s.length === 10 ? s + "T00:00:00" : s) : null;

// The DISPLAY MOMENT for a forecast's resolution — where the vertical forecast
// line (and its % pie) lands on the timeline, at better-than-day precision. This
// differs from parseResolveDateLocal (which is a pure day-granularity boundary
// used by the past-date guard, resolver poll, tracking, etc. — do NOT swap those):
//
//   • Explicit time given (full ISO with a non-midnight clock) → that exact
//     instant. "Resolves 2027-06-30T14:00:00Z" lands at 2pm, not day-boundary.
//   • "Does X happen BEFORE <date>?" → LOCAL START of the date (00:00). The window
//     closes at the start of the date, so the line sits at that midnight.
//   • Any other date-only resolution → END of the date, represented as the NEXT
//     local midnight. A date-only resolution is open THROUGH its whole day, so a
//     resolution of *today* must sit to the RIGHT of the live "today" line (which
//     tracks the current time) instead of collapsing to this morning's midnight
//     (left of "today"). Returning next-midnight (rather than 23:59:59.999) keeps
//     day-granularity placement + the event clamp aligned on the following tick —
//     the same shift the old Dec-31→Jan-1 special-case applied, now generalized.
//
// Returns a Date, or null for missing/invalid input. `question` is optional; the
// "before" rule only fires when it's supplied and contains the word "before".
export const resolutionMomentLocal = (resolution_date, question = "") => {
  if (!resolution_date) return null;
  const s = typeof resolution_date === "string" ? resolution_date : null;
  // A full ISO timestamp carrying a real (non-midnight) time → honor it exactly.
  // A bare-midnight timestamp ("…T00:00:00Z") is really just a date, so it falls
  // through to the date-only convention below.
  if (s && s.length > 10 && !BARE_MIDNIGHT.test(s)) {
    const exact = new Date(s);
    return isNaN(exact.getTime()) ? null : exact;
  }
  const dayStr = s ? s.slice(0, 10) : null;
  const base = dayStr ? new Date(dayStr + "T00:00:00") : new Date(resolution_date);
  if (isNaN(base.getTime())) return null;
  if (/\bbefore\b/i.test(question || "")) return base; // start of the date (local 00:00)
  base.setDate(base.getDate() + 1); // end of the date == next local midnight
  return base;
};

// Serialize a resolution-line boundary (from resolutionMomentLocal) to a string
// for clamping event dates ONTO the line. ALWAYS a LOCAL timestamp
// (YYYY-MM-DDTHH:MM:SS[.SSS], no timezone suffix) — deliberately NOT a bare
// YYYY-MM-DD day-string, even for a midnight boundary: event dates are positioned
// via `new Date(ev.date)` (eventAnchorMs / dayToScreen), and `new Date("YYYY-MM-DD")`
// is UTC midnight, which would offset a clamped event from the LOCAL-midnight
// boundary (the line) by the timezone offset in non-UTC zones. A local timestamp
// reconstructs the exact boundary instant via BOTH new Date and
// parseResolveDateLocal, so clamped events land exactly on the line. Milliseconds
// are included only when non-zero so a fractional-second boundary round-trips too.
export const resolutionClampIso = (boundary) => {
  if (!boundary || isNaN(boundary.getTime())) return null;
  const p = (n) => String(n).padStart(2, "0");
  const ms = boundary.getMilliseconds();
  const frac = ms ? `.${String(ms).padStart(3, "0")}` : "";
  return (
    `${boundary.getFullYear()}-${p(boundary.getMonth() + 1)}-${p(boundary.getDate())}` +
    `T${p(boundary.getHours())}:${p(boundary.getMinutes())}:${p(boundary.getSeconds())}${frac}`
  );
};

// Relative-time formatter for the meta-line "lastRun" string. Server is
// expected to provide a proper lastReforecastedAt eventually; meanwhile
// we derive from the timestamp we already track for the cooldown gate.
//
// `now` is INJECTABLE and defaults to the clock, so the same function serves a
// live render and a deterministic test. It was previously clock-only, which is
// what stopped CopyLinksPanel's "created" line from being asserted at all from a
// static render — the caller could not pin the answer. Every existing call site
// omits the argument and is unaffected; a caller that passes a non-finite `now`
// gets "" rather than "NaNd ago", the same fail-quiet direction as a bad `iso`.
export const fmtRelative = (iso, now = Date.now()) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t) || !Number.isFinite(now)) return "";
  const s = Math.floor((now - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// How a resolved deadline reads to the person who set it: the exact moment in
// the zone it was decided in, plus WHY that zone was chosen.
//
// This exists so a wrong guess is correctable. The server infers the zone from
// the question ("market close" -> New York's bell) or from the asker's browser,
// and an inference nobody can see is one nobody can challenge. `basis` is the
// rule that fired — see contract_server/resolution_time.py.
//
// Returns "" when there is nothing resolved to show, so a caller can render it
// conditionally without a guard.
const RESOLUTION_BASIS_LABELS = {
  explicit: "as you stated it",
  event: "scheduled event time",
  event_estimate: "estimated end of the event",
  subject_zone: "the subject's timezone",
  asker_zone: "your timezone",
  fallback: "UTC — timezone unknown",
};

export const describeResolutionMoment = (isoMoment, zone, basis, label = "") => {
  if (!isoMoment) return "";
  const d = new Date(isoMoment);
  if (isNaN(d.getTime())) return "";
  let stamp;
  // Set when the recorded zone will not load: the clock below is then UTC, so the
  // REASON must say so too. Keeping the original basis would render a UTC time
  // labelled "your timezone" — a line that is wrong in exactly the way this line
  // exists to prevent, and more misleading than showing no reason at all.
  let zoneFailed = false;
  try {
    stamp = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone || "UTC",
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    // An unloadable zone must not break the panel; fall back to UTC.
    stamp = d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
    zoneFailed = true;
  }
  // A plain-words label ("Tokyo Stock Exchange close") says more than the rule name,
  // but never over a zone failure, whose reason must stay the UTC warning.
  const why = zoneFailed
    ? RESOLUTION_BASIS_LABELS.fallback
    : label || RESOLUTION_BASIS_LABELS[basis];
  return why ? `${stamp} (${why})` : stamp;
};
